import "@/App.css";
import { useEffect } from "react";
import { BrowserRouter, Route, Routes, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { SiteProvider } from "@/lib/SiteContext";
import { BasketProvider } from "@/lib/BasketContext";
import { Layout } from "@/components/layout/Layout";
import Home from "@/pages/Home";
import Shop from "@/pages/Shop";
import About from "@/pages/About";
import Events from "@/pages/Events";
import Contact from "@/pages/Contact";
import Faqs from "@/pages/Faqs";
import AllergenInfo from "@/pages/AllergenInfo";
import DeliveryInfo from "@/pages/DeliveryInfo";
import Legal from "@/pages/Legal";
import Basket from "@/pages/Basket";
import NotFound from "@/pages/NotFound";
import AdminLogin from "@/pages/admin/Login";
import AdminForgotPassword from "@/pages/admin/ForgotPassword";
import AdminResetPassword from "@/pages/admin/ResetPassword";
import { AdminLayout } from "@/pages/admin/AdminLayout";
import AdminDashboard from "@/pages/admin/Dashboard";
import AdminProducts from "@/pages/admin/Products";
import AdminCatalogueReview from "@/pages/admin/CatalogueReview";
import AdminProductEdit from "@/pages/admin/ProductEdit";
import AdminProductPreview from "@/pages/admin/ProductPreview";
import AdminCategories from "@/pages/admin/Categories";
import AdminSlots from "@/pages/admin/Slots";
import AdminOrders from "@/pages/admin/Orders";
import AdminOrderDetail from "@/pages/admin/OrderDetail";
import AdminProductLabel from "@/pages/admin/ProductLabel";
import AdminProduction from "@/pages/admin/Production";
import AdminPackingSheet from "@/pages/admin/PackingSheet";
import AdminEmails from "@/pages/admin/Emails";
import AdminNewsletter from "@/pages/admin/Newsletter";
import AdminPhotos from "@/pages/admin/Photos";
import AdminAvailability from "@/pages/admin/Availability";
import AdminDeliveryCollection from "@/pages/admin/DeliveryCollection";
import AdminContent from "@/pages/admin/Content";
import AdminSettings from "@/pages/admin/Settings";
import AdminAccount from "@/pages/admin/Account";
import AdminHelp from "@/pages/admin/Help";
import AdminAdvanced from "@/pages/admin/Advanced";
import Unsubscribe from "@/pages/Unsubscribe";
import ProductDetails from "@/pages/ProductDetails";
import Checkout from "@/pages/Checkout";
import OrderConfirmation from "@/pages/OrderConfirmation";

const PAGE_TITLES = {
  "/": "Bakes by CR | Handmade Brownies, Cookies & Grazing Tables",
  "/shop": "Shop Bakes | Bakes by CR",
  "/about": "Our Story | Bakes by CR",
  "/events": "Grazing Tables & Events | Bakes by CR",
  "/faqs": "FAQs | Bakes by CR",
  "/delivery-collection": "Delivery & Collection | Bakes by CR",
  "/contact": "Contact | Bakes by CR",
  "/basket": "Your Basket | Bakes by CR",
  "/checkout": "Checkout | Bakes by CR",
};

const PageTitle = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    const isPrivate = pathname.startsWith("/admin") || pathname.startsWith("/order") || pathname.startsWith("/legal");
    if (!isPrivate) {
      document.title = PAGE_TITLES[pathname] || (pathname.startsWith("/shop/") ? "Product | Bakes by CR" : "Page not found | Bakes by CR");
    }
  }, [pathname]);
  return null;
};

function App() {
  return (
    <SiteProvider>
      <BasketProvider>
        <BrowserRouter>
          <Toaster position="top-center" toastOptions={{ style: { background: "#26150F", color: "#F8F3ED", border: "1px solid #4A2E22" } }} />
          <PageTitle />
          <Routes>
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/admin/forgot-password" element={<AdminForgotPassword />} />
            <Route path="/admin/reset-password" element={<AdminResetPassword />} />
            <Route element={<AdminLayout />}>
              <Route path="/admin" element={<AdminDashboard />} />
              <Route path="/admin/products" element={<AdminProducts />} />
              <Route path="/admin/catalogue-review" element={<AdminCatalogueReview />} />
              <Route path="/admin/products/new" element={<AdminProductEdit />} />
              <Route path="/admin/products/:id" element={<AdminProductEdit />} />
              <Route path="/admin/products/:id/preview" element={<AdminProductPreview />} />
              <Route path="/admin/products/:id/label" element={<AdminProductLabel />} />
              <Route path="/admin/categories" element={<AdminCategories />} />
              <Route path="/admin/slots" element={<AdminSlots />} />
              <Route path="/admin/delivery" element={<AdminDeliveryCollection />} />
              <Route path="/admin/orders" element={<AdminOrders />} />
              <Route path="/admin/orders/:id" element={<AdminOrderDetail />} />
              <Route path="/admin/production" element={<AdminProduction />} />
              <Route path="/admin/production/packing/:orderId" element={<AdminPackingSheet />} />
              <Route path="/admin/emails" element={<AdminEmails />} />
              <Route path="/admin/newsletter" element={<AdminNewsletter />} />
              <Route path="/admin/photos" element={<AdminPhotos />} />
              <Route path="/admin/availability" element={<AdminAvailability />} />
              <Route path="/admin/content" element={<AdminContent />} />
              <Route path="/admin/settings" element={<AdminSettings />} />
              <Route path="/admin/account" element={<AdminAccount />} />
              <Route path="/admin/help" element={<AdminHelp />} />
              <Route path="/admin/advanced" element={<AdminAdvanced />} />
            </Route>
            <Route element={<Layout />}>
              <Route path="/" element={<Home />} />
              <Route path="/shop" element={<Shop />} />
              <Route path="/shop/:slug" element={<ProductDetails />} />
              <Route path="/checkout" element={<Checkout />} />
              <Route path="/order/:number" element={<OrderConfirmation />} />
              <Route path="/about" element={<About />} />
              <Route path="/events" element={<Events />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/faqs" element={<Faqs />} />
              <Route path="/allergens" element={<AllergenInfo />} />
              <Route path="/delivery-collection" element={<DeliveryInfo />} />
              <Route path="/privacy-policy" element={<Legal kind="privacy" />} />
              <Route path="/cookie-policy" element={<Legal kind="cookies" />} />
              <Route path="/terms" element={<Legal kind="terms" />} />
              <Route path="/legal/terms" element={<Legal kind="terms" />} />
              <Route path="/legal/cancellation-refunds" element={<Legal kind="refunds" />} />
              <Route path="/legal/delivery-collection" element={<Legal kind="delivery" />} />
              <Route path="/legal/allergen-information" element={<Legal kind="allergens" />} />
              <Route path="/legal/privacy" element={<Legal kind="privacy" />} />
              <Route path="/legal/cookies" element={<Legal kind="cookies" />} />
              <Route path="/basket" element={<Basket />} />
              <Route path="/unsubscribe" element={<Unsubscribe />} />
              <Route path="*" element={<NotFound />} />
            </Route>
          </Routes>
        </BrowserRouter>
      </BasketProvider>
    </SiteProvider>
  );
}

export default App;
